import React, { useEffect, useRef, useState } from 'react';
import { API, TOKEN_KEY } from '../../contexts/AuthContext';

// Video de UMA camera. Tres modos:
//   ao vivo (padrao)  : stream extra, o do mosaico;
//   ao vivo `hd`      : stream principal, camera aberta sozinha;
//   gravacao `inicio` : gravacao do DVR a partir de um horario (player do
//                       recorte). Nao reconecta: quando o trecho acaba, acabou.
//
// O video chega por WebSocket (/ws/cameras no backend, que faz a ponte com o
// go2rtc) como pedacos de fMP4 e e tocado com Media Source Extensions. O
// protocolo e o do go2rtc: mandamos { type: 'mse', value: <codecs que este
// navegador toca> }, ele responde com o codec escolhido e passa a mandar
// binario.

// So video: a exibicao e muda, pedir audio seria banda jogada fora.
const CODECS = [
  'avc1.640029', // H.264 high 4.1
  'avc1.64002A', // H.264 high 4.2
  'avc1.640033', // H.264 high 5.1
  'hvc1.1.6.L153.B0', // H.265 (so toca onde o navegador tem decodificador)
];

// Reconexao com espera CRESCENTE (5s, 10s, 20s... ate 2 min). Com espera fixa
// de 5s, um canal com problema virava milhares de logins RTSP no gravador
// num fim de semana de tour ligado na TV.
const RECONECTAR_MS = 5000;
const RECONECTAR_MAX_MS = 2 * 60 * 1000;
// Senha recusada: o gravador Intelbras bloqueia o usuario depois de algumas
// tentativas erradas. O gravador inteiro (todos os tiles e as proximas voltas
// do tour) para de tentar por este tempo.
const PAUSA_SENHA_MS = 10 * 60 * 1000;
export const ERRO_SENHA = 'Usuário ou senha recusados';
export const ERRO_CODEC = 'Codec não suportado (use H.264)';
// gravadorId -> horario ate quando nao tentar (compartilhado entre os tiles e
// sobrevive a remontagem do mosaico a cada volta do tour).
const pausaPorGravador = new Map();

// Sem nenhum pedaco de video por este tempo = stream morto (gravador caiu,
// rota sumiu). Derruba e tenta de novo.
const SEM_DADOS_MS = 12000;
const VIGIA_MS = 4000;
// Segundos de video mantidos no buffer. E ao vivo: o que passou nao interessa,
// e sem podar o buffer cresce ate o navegador recusar novos pedacos.
const JANELA_BUFFER_S = 5;
// Atraso tolerado em relacao ao ao vivo antes de pular para o fim do buffer.
const ATRASO_MAX_S = 2.5;
const FILA_MAX_BYTES = 8 * 1024 * 1024;

function wsUrl(cameraId, { hd, inicio }) {
  // Token lido a cada conexao: o tour roda por dias e a sessao e renovada
  // no meio do caminho (ver Cameras.js).
  const token = localStorage.getItem(TOKEN_KEY) || '';
  const base = API.replace(/^http/i, 'ws').replace(/\/+$/, '');
  let url = `${base}/ws/cameras?token=${encodeURIComponent(token)}&camera=${cameraId}`;
  if (hd) url += '&hd=1';
  if (inicio) url += `&inicio=${encodeURIComponent(inicio)}`;
  return url;
}

// Traduz o erro cru do go2rtc para algo que quem olha a TV entende.
function traduzirErro(valor) {
  const v = String(valor || '').toLowerCase();
  if (v.includes('codecs not matched')) return ERRO_CODEC;
  if (/401|unauthorized|wrong user|wrong pass/.test(v)) return ERRO_SENHA;
  if (/404|not found/.test(v)) return 'Sem gravação neste horário';
  if (/timeout|refused|no route|unreachable|no such host/.test(v)) return 'Equipamento inacessível';
  return 'Sem sinal';
}

function motivoDoFechamento(codigo, gravacao) {
  switch (codigo) {
    case 4001: return 'Sessão expirada';
    case 4003: return 'Sem permissão';
    case 4004: return 'Câmera não encontrada';
    case 4502: return 'Gateway de vídeo fora do ar';
    default: return gravacao ? 'Fim da gravação (ou sem gravação neste horário)' : 'Sem sinal';
  }
}

export default function CameraPlayer({
  camera, hd = false, inicio = null, rotulo, onClick, onTempo, onErro, ajuste = 'contain',
}) {
  const videoRef = useRef(null);
  const [estado, setEstado] = useState('conectando'); // conectando | ao-vivo | erro
  const [mensagem, setMensagem] = useState('');
  // Callbacks em ref: trocar a funcao a cada render nao pode reconectar o video.
  const onTempoRef = useRef(onTempo);
  const onErroRef = useRef(onErro);
  onTempoRef.current = onTempo;
  onErroRef.current = onErro;

  const cameraId = camera.id;
  const gravadorId = camera.gravador_id;

  useEffect(() => {
    const video = videoRef.current;
    const gravacao = !!inicio;
    let desmontado = false;
    let ws = null;
    let objectUrl = null;
    let timerReconexao = null;
    let timerVigia = null;
    let tentativas = 0;
    // Tempo de midia do primeiro quadro: posicao na gravacao = currentTime - t0.
    let t0 = null;

    const soltar = () => {
      clearInterval(timerVigia);
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        try { ws.close(); } catch (_) { /* ja fechado */ }
        ws = null;
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    };

    const falhar = (texto, permanente = false) => {
      if (desmontado) return;
      soltar();
      setEstado('erro');
      setMensagem(texto);
      if (onErroRef.current) onErroRef.current(texto);
      clearTimeout(timerReconexao);
      // Gravacao nao reconecta: recomecaria do inicio do trecho.
      if (permanente || gravacao) return;

      if (texto === ERRO_SENHA) pausaPorGravador.set(gravadorId, Date.now() + PAUSA_SENHA_MS);
      tentativas += 1;
      const espera = Math.min(RECONECTAR_MS * 2 ** (tentativas - 1), RECONECTAR_MAX_MS);
      timerReconexao = setTimeout(conectar, espera);
    };

    const conectar = () => {
      if (desmontado) return;
      soltar();

      // Gravador em pausa por senha recusada: nem tenta, so agenda para
      // quando a pausa acabar.
      const pausaAte = pausaPorGravador.get(gravadorId) || 0;
      if (pausaAte > Date.now()) {
        setEstado('erro');
        setMensagem(ERRO_SENHA);
        timerReconexao = setTimeout(conectar, pausaAte - Date.now());
        return;
      }

      // ManagedMediaSource e o que existe no Safari do iPhone.
      const Fonte = window.ManagedMediaSource || window.MediaSource;
      if (!Fonte) {
        falhar('Navegador sem suporte a vídeo ao vivo', true);
        return;
      }
      const codecs = CODECS.filter((c) => Fonte.isTypeSupported(`video/mp4; codecs="${c}"`)).join();

      const ms = new Fonte();
      const socket = new WebSocket(wsUrl(cameraId, { hd, inicio }));
      socket.binaryType = 'arraybuffer';
      ws = socket;

      let sb = null;
      let fila = [];
      let filaBytes = 0;
      let ultimoDado = Date.now();
      let fonteAberta = false;
      let pediu = false;

      // So da para pedir o codec com as DUAS pontas prontas (socket e
      // MediaSource), e elas abrem em ordem imprevisivel.
      const pedirCodec = () => {
        if (pediu || !fonteAberta || socket.readyState !== WebSocket.OPEN) return;
        pediu = true;
        socket.send(JSON.stringify({ type: 'mse', value: codecs }));
      };

      ms.addEventListener('sourceopen', () => {
        fonteAberta = true;
        pedirCodec();
      }, { once: true });

      if (window.ManagedMediaSource) {
        video.disableRemotePlayback = true;
        video.srcObject = ms;
      } else {
        objectUrl = URL.createObjectURL(ms);
        video.src = objectUrl;
      }

      const anexar = (dados) => {
        try {
          sb.appendBuffer(dados);
        } catch (err) {
          // Tipicamente QuotaExceededError: buffer cheio. Recomecar e mais
          // seguro do que tentar consertar um SourceBuffer nesse estado.
          console.warn(`[cameras] buffer recusou video (camera ${cameraId}):`, err.name);
          falhar('Reconectando…');
        }
      };

      const aoAtualizar = () => {
        if (!sb || sb.updating) return;

        if (fila.length) {
          const junto = new Uint8Array(filaBytes);
          let pos = 0;
          for (const parte of fila) {
            junto.set(parte, pos);
            pos += parte.byteLength;
          }
          fila = [];
          filaBytes = 0;
          anexar(junto);
          return;
        }

        if (!sb.buffered.length) return;
        const fim = sb.buffered.end(sb.buffered.length - 1);
        const inicioBuffer = sb.buffered.start(0);
        if (t0 === null) t0 = inicioBuffer;
        const corte = fim - JANELA_BUFFER_S;
        if (corte > inicioBuffer) {
          try { sb.remove(inicioBuffer, corte); } catch (_) { /* fonte fechando */ }
        }
        // Ficou para tras (aba em segundo plano, engasgo de rede): volta para
        // o ponto mais recente em vez de exibir imagem atrasada.
        if (video.currentTime < corte || fim - video.currentTime > ATRASO_MAX_S) {
          video.currentTime = Math.max(inicioBuffer, fim - 0.5);
        }
      };

      socket.onopen = pedirCodec;

      socket.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          let msg;
          try { msg = JSON.parse(ev.data); } catch (_) { return; }

          if (msg.type === 'mse') {
            try {
              sb = ms.addSourceBuffer(msg.value);
              sb.mode = 'segments';
              sb.addEventListener('updateend', aoAtualizar);
            } catch (err) {
              console.warn(`[cameras] codec recusado pelo navegador (${msg.value}):`, err.name);
              falhar(ERRO_CODEC, true);
            }
          } else if (msg.type === 'error') {
            console.warn(`[cameras] camera ${cameraId}: ${msg.value}`);
            const texto = traduzirErro(msg.value);
            falhar(texto, texto === ERRO_CODEC);
          }
          return;
        }

        ultimoDado = Date.now();
        if (!sb) return;
        if (sb.updating || fila.length) {
          const parte = new Uint8Array(ev.data);
          fila.push(parte);
          filaBytes += parte.byteLength;
          if (filaBytes > FILA_MAX_BYTES) falhar('Reconectando…');
        } else {
          anexar(ev.data);
        }
      };

      socket.onclose = (ev) => falhar(motivoDoFechamento(ev.code, gravacao), ev.code === 4003);

      timerVigia = setInterval(() => {
        if (Date.now() - ultimoDado > SEM_DADOS_MS) {
          falhar(gravacao ? 'Fim da gravação (ou sem gravação neste horário)' : 'Sem sinal');
          return;
        }
        // `autoPlay` so vale na montagem; depois de reconectar e preciso
        // mandar tocar de novo.
        if (sb && video.paused) video.play().catch(() => { /* tenta de novo no proximo tique */ });
      }, VIGIA_MS);

      setEstado('conectando');
      setMensagem('');
      video.play().catch(() => { /* a vigia tenta de novo */ });
    };

    const aoTocar = () => {
      if (desmontado) return;
      tentativas = 0;
      setEstado('ao-vivo');
    };
    const aoAvancar = () => {
      if (t0 !== null && onTempoRef.current) onTempoRef.current(Math.max(0, video.currentTime - t0));
    };
    video.addEventListener('playing', aoTocar);
    video.addEventListener('timeupdate', aoAvancar);

    conectar();

    return () => {
      desmontado = true;
      clearTimeout(timerReconexao);
      video.removeEventListener('playing', aoTocar);
      video.removeEventListener('timeupdate', aoAvancar);
      soltar();
    };
  }, [cameraId, gravadorId, hd, inicio]);

  const Raiz = onClick ? 'button' : 'div';
  return (
    <Raiz
      type={onClick ? 'button' : undefined}
      className={`camera-tile camera-tile--${estado} ${onClick ? 'camera-tile--clicavel' : ''}`}
      onClick={onClick}
      title={onClick ? `Abrir ${rotulo || camera.nome}` : undefined}
    >
      <video ref={videoRef} muted autoPlay playsInline style={{ objectFit: ajuste }} />
      {estado !== 'ao-vivo' && (
        <div className="camera-tile__aviso">
          {estado === 'conectando'
            ? <div className="screen-loader__spinner" />
            : <i className="bi bi-camera-video-off" />}
          <span>{estado === 'conectando' ? 'Conectando…' : mensagem}</span>
        </div>
      )}
      {rotulo ? <span className="camera-tile__rotulo">{rotulo}</span> : null}
    </Raiz>
  );
}
